import React, { useEffect, useState } from 'react';
import { GeoJSON } from 'react-leaflet';
import type { Feature, FeatureCollection } from 'geojson';
import type L from 'leaflet';

import { BOUNDARIES_PANE } from 'components/InteractiveMap/MapPanes';

import './index.scss';

// GeoJSON dos estados brasileiros (Code for America, MIT). Servido via jsDelivr
// para cache global. Quando este projeto puder hospedar localmente, mover para
// public/mock/brazil-states.geojson e trocar a URL por '/mock/brazil-states.geojson'.
const BRAZIL_STATES_URL = 'https://cdn.jsdelivr.net/gh/codeforamerica/click_that_hood@master/public/data/brazil-states.geojson';

const STATE_ABBR: Record<string, string> = {
  Acre: 'AC',
  Alagoas: 'AL',
  Amapá: 'AP',
  Amazonas: 'AM',
  Bahia: 'BA',
  Ceará: 'CE',
  'Distrito Federal': 'DF',
  'Espírito Santo': 'ES',
  Goiás: 'GO',
  Maranhão: 'MA',
  'Mato Grosso': 'MT',
  'Mato Grosso do Sul': 'MS',
  'Minas Gerais': 'MG',
  Pará: 'PA',
  Paraíba: 'PB',
  Paraná: 'PR',
  Pernambuco: 'PE',
  Piauí: 'PI',
  'Rio de Janeiro': 'RJ',
  'Rio Grande do Norte': 'RN',
  'Rio Grande do Sul': 'RS',
  Rondônia: 'RO',
  Roraima: 'RR',
  'Santa Catarina': 'SC',
  'São Paulo': 'SP',
  Sergipe: 'SE',
  Tocantins: 'TO',
};

const lineStyle = {
  color: '#ffffff', // tema: fixo (desenhado sobre o mapa)
  weight: 1.2,
  opacity: 0.85,
  fillOpacity: 0,
  interactive: false,
};

interface LayerWithTooltip {
  bindTooltip?: (content: string, opts?: L.TooltipOptions) => unknown;
}

function attachStateLabel(feature: Feature, layer: L.Layer) {
  const name = (feature.properties as { name?: string } | undefined)?.name;
  if (!name) return;
  const sigla = STATE_ABBR[name] ?? name;
  const target = layer as L.Layer & LayerWithTooltip;
  if (typeof target.bindTooltip !== 'function') return;
  target.bindTooltip(sigla, {
    permanent: true,
    direction: 'center',
    className: 'brazil-state-label',
    interactive: false,
  });
}

export default function BrazilStatesLayer() {
  const [data, setData] = useState<FeatureCollection | null>(null);

  useEffect(() => {
    let canceled = false;
    fetch(BRAZIL_STATES_URL)
      .then((r) => r.json())
      .then((json: FeatureCollection) => { if (!canceled) setData(json); })
      .catch(() => { if (!canceled) setData(null); });
    return () => { canceled = true; };
  }, []);

  if (!data) return null;

  return (
    <GeoJSON
      data={data}
      style={() => lineStyle}
      onEachFeature={attachStateLabel}
      pane={BOUNDARIES_PANE}
    />
  );
}
